package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.repository.ShiftNoteRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.team.SaveShiftNoteUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_save_failed
import evoshift.composeapp.generated.resources.shift_note_removed
import evoshift.composeapp.generated.resources.shift_note_saved
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/** Which shift's note (one view model per shift). */
data class ShiftNoteKey(val workplaceId: String, val shiftId: String)

data class ShiftNoteUiState(
    val isLoading: Boolean = true,
    /** The note as saved (empty: none). */
    val note: String = "",
    val isSaving: Boolean = false,
    /** "Saved" / "Removed", or why it failed; shown under the field. */
    val message: UiText? = null,
    val isError: Boolean = false
)

/**
 * A manager's note on one shift for the person working it (SH-118), in the roster builder's
 * shift dialog. Saved on its own (not with the dialog's Save), optimistically; a failed save
 * brings the earlier note back.
 */
class ShiftNoteViewModel(
    private val key: ShiftNoteKey,
    private val repository: ShiftNoteRepository,
    private val saveShiftNote: SaveShiftNoteUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase
) : ViewModel() {

    private val _uiState = MutableStateFlow(ShiftNoteUiState())
    val uiState: StateFlow<ShiftNoteUiState> = _uiState.asStateFlow()

    init {
        viewModelScope.launch {
            val note = repository.get(key.workplaceId, key.shiftId).getOrNull()?.note.orEmpty()
            _uiState.update { it.copy(isLoading = false, note = note) }
        }
    }

    fun save(text: String) {
        val before = _uiState.value.note
        val after = text.trim()
        if (after == before || _uiState.value.isSaving) return
        _uiState.update { it.copy(note = after, isSaving = true, message = null, isError = false) }
        viewModelScope.launch {
            val editor = getCurrentUserUseCase().first()?.uid.orEmpty()
            saveShiftNote(key.workplaceId, key.shiftId, after, editor)
                .onSuccess {
                    _uiState.update {
                        it.copy(isSaving = false, message = UiText.Resource(if (after.isEmpty()) Res.string.shift_note_removed else Res.string.shift_note_saved))
                    }
                }
                .onFailure { e ->
                    _uiState.update {
                        it.copy(note = before, isSaving = false, isError = true, message = UiText.Resource(Res.string.error_save_failed, e.toUiText()))
                    }
                }
        }
    }
}
